import { SelectOption } from '@/constants';

/* ------------------------------------------------------------------------ */
/*  Regions + countries                                                     */
/* ------------------------------------------------------------------------ */
export interface Country<T extends string = string> {
  readonly code: T;
  readonly name: string;
}

export interface CloudRegion<
  P extends string | null = string | null,
  C extends Country = Country
> {
  readonly prefix: P; // e.g. 'europe' | 'us' | null
  readonly label: string; // human-readable
  readonly countries: readonly C[];
}

export const CLOUD_REGIONS = [
  // ––––– placeholder “no preference” region –––––
  {
    prefix: null,
    label: 'Default region (from org settings)',
    countries: [] as const,
  },
  // ––––– real regions –––––
  {
    prefix: 'africa',
    label: 'Africa',
    countries: [] as const,
  },
  {
    prefix: 'asia',
    label: 'Asia',
    countries: [] as const,
  },
  {
    prefix: 'europe',
    label: 'Europe',
    countries: [
      { code: 'AT', name: 'Austria' },
      { code: 'CZ', name: 'Czech Republic' },
      { code: 'DE', name: 'Germany' },
      { code: 'CH', name: 'Switzerland' },
    ] as const,
  },
  {
    prefix: 'me',
    label: 'Middle East',
    countries: [] as const,
  },
  {
    prefix: 'northamerica',
    label: 'North America',
    countries: [{ code: 'CA', name: 'Canada' }] as const,
  },
  {
    prefix: 'southamerica',
    label: 'South America',
    countries: [] as const,
  },
  {
    prefix: 'us',
    label: 'United States',
    countries: [{ code: 'US', name: 'United States' }] as const,
  },
] as const satisfies readonly CloudRegion[];

// literal-type unions
export type RegionPrefix = (typeof CLOUD_REGIONS)[number]['prefix']; // 'europe' | 'us' | … | null
export type NonNullRegionPrefix = Exclude<RegionPrefix, null>;
export type RegionLabel = (typeof CLOUD_REGIONS)[number]['label']; // 'Europe' | 'United States' | … | null
export type NonNullRegionLabel = Exclude<RegionLabel, null>;

export type CountryCode =
  (typeof CLOUD_REGIONS)[number]['countries'][number]['code']; // 'AT' | 'US' | …
export type NonNullCountryCode = Exclude<CountryCode, null>;

export type CountryOption = SelectOption<CountryCode | null>;

/* ------------------------------------------------------------------------ */
/*  Flatten all *real* countries                                            */
/* ------------------------------------------------------------------------ */
const ALL_COUNTRIES = CLOUD_REGIONS.flatMap<Country<NonNullCountryCode>>(
  (r) => r.countries
) as readonly Country<NonNullCountryCode>[];

/* ------------------------------------------------------------------------ */
/*  <select> / <b-form-select> helpers                                      */
/* ------------------------------------------------------------------------ */
export const CloudRegionPrefixSelectOptions: readonly SelectOption<RegionPrefix>[] =
  [
    { text: 'Default region (from organization settings)', value: null },
    ...CLOUD_REGIONS.filter((r) => r.prefix !== null).map((r) => ({
      text: r.label,
      value: r.prefix,
    })), // typed automatically
  ] as const;

export const CountrySelectOptions: readonly CountryOption[] = [
  { text: 'Select Country', value: null },
  ...ALL_COUNTRIES.map((c): CountryOption => ({ text: c.name, value: c.code })),
] as const;

/* ------------------------------------------------------------------------ */
/*  Look-ups & helpers                                                      */
/* ------------------------------------------------------------------------ */

/** Map 'AT' → 'europe', 'US' → 'us', …  (null codes are deliberately skipped) */
export const COUNTRY_TO_REGION_PREFIX: Readonly<
  Record<NonNullCountryCode, NonNullRegionPrefix>
> = Object.fromEntries(
  CLOUD_REGIONS.flatMap((r) =>
    r.prefix === null // skip the placeholder region
      ? []
      : r.countries.map((c) => [c.code, r.prefix])
  )
) as Record<NonNullCountryCode, NonNullRegionPrefix>;

export const COUNTRY_TO_REGION_LABEL: Readonly<
  Record<NonNullCountryCode, NonNullRegionLabel>
> = Object.fromEntries(
  CLOUD_REGIONS.flatMap((r) =>
    r.label === null // skip the placeholder region
      ? []
      : r.countries.map((c) => [c.code, r.label])
  )
) as Record<NonNullCountryCode, NonNullRegionLabel>;

/** Get region label directly from prefix */
export function regionLabel(prefix: RegionPrefix): string {
  return CLOUD_REGIONS.find((r) => r.prefix === prefix)!.label;
}

/* ------------------------------------------------------------------ */
/*  Narrow an arbitrary string to the literal union NonNullCountryCode */
/* ------------------------------------------------------------------ */
function isNonNullCountryCode(x: string): x is NonNullCountryCode {
  return Object.prototype.hasOwnProperty.call(COUNTRY_TO_REGION_PREFIX, x);
}

/* ------------------------------------------------------------------ */
/*  Country-code  →  region-prefix                                    */
/* ------------------------------------------------------------------ */
export function countryToRegionPrefix(
  code: string | CountryCode | null | undefined
): RegionPrefix /* == string | null */ {
  if (!code) return null; // '', null, undefined

  // Normalise – many APIs hand back lower-case codes
  const cc = code.toUpperCase();

  // Use the type-guard to convince TS we only index with valid keys
  return isNonNullCountryCode(cc)
    ? COUNTRY_TO_REGION_PREFIX[cc] // cc is NonNullCountryCode here
    : null; // unknown / unmapped country
}

export function countryToRegionLabel(
  code: string | CountryCode | null | undefined
): RegionLabel | null /* == string | null */ {
  if (!code) return null; // '', null, undefined

  // Normalise – many APIs hand back lower-case codes
  const cc = code.toUpperCase();

  // Use the type-guard to convince TS we only index with valid keys
  return isNonNullCountryCode(cc)
    ? COUNTRY_TO_REGION_LABEL[cc] // cc is NonNullCountryCode here
    : null; // unknown / unmapped country
}
